/**
 * ShareDialog.tsx
 * 
 * This component provides functionality for sharing posits with other users.
 * 
 * Changes:
 * - Refactored to use smaller, more focused components
 * - Moved email sharing form to ShareEmailForm
 * - Moved shared users list to SharedUsersList
 * - Moved premium upgrade prompt to PremiumUpgradePrompt
 * - Moved share link section to ShareLinkSection
 * - Added embed functionality with EmbedSection
 */

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";

// Import refactored components
import { ShareLinkSection } from "./ShareLinkSection";
import { ShareEmailForm } from "./ShareEmailForm";
import { SharedUsersList } from "./SharedUsersList";
import { PremiumUpgradePrompt } from "./PremiumUpgradePrompt";
import { EmbedSection } from "./EmbedSection";

interface ShareDialogProps {
  positId: string;
  isPremium: boolean;
  buttonClassName?: string;
  buttonSize?: "sm" | "default" | "lg";
  showLabel?: boolean;
  iconSize?: "sm" | "default";
}

interface SharedUser {
  id: string;
  email: string;
}

interface PositData {
  privacy: string;
  title: string;
}

const ShareDialog = ({ positId, isPremium, buttonClassName = "w-9 px-0", buttonSize, showLabel = false, iconSize = "default" }: ShareDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [shareUrl, setShareUrl] = useState("");

  // Query to fetch posit data
  const { data: positData } = useQuery({
    queryKey: ["posit-data", positId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posits")
        .select("privacy, title")
        .eq("id", positId)
        .single();

      if (error) throw error;
      return data as PositData;
    },
    enabled: isOpen,
  });

  // Query to fetch shared users
  const { data: sharedUsers, isLoading: isLoadingShares } = useQuery({
    queryKey: ["posit-shares", positId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posit_shares")
        .select("id, user_email")
        .eq("posit_id", positId);

      if (error) throw error;
      
      // Transform data to match our SharedUser interface
      return (data?.filter(share => share.user_email !== user?.email) || [])
        .map(share => ({ 
          id: share.id, 
          email: share.user_email 
        }));
    },
    enabled: isOpen,
  });

  useEffect(() => {
    if (positId) {
      setShareUrl(`${window.location.origin}/chat/${positId}`);
    }
  }, [positId]);

  const handleRefreshSharedUsers = () => {
    queryClient.invalidateQueries({ queryKey: ["posit-shares", positId] });
    if (user) {
      queryClient.invalidateQueries({ queryKey: ["my-posit", user.id] });
    }
  };

  const isPublic = positData?.privacy === 'public';

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size={buttonSize ?? "sm"} className={buttonClassName}>
          <Upload className={iconSize === "sm" ? "w-3 h-3" : "h-4 w-4"} />
          {showLabel && <span className="ml-1">Share</span>}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Share Posit</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 pt-4">
          <div className="space-y-2">
            <ShareLinkSection shareUrl={shareUrl} />
          </div>

          <div className="h-px bg-border" />

          <div className="space-y-2">
            <EmbedSection 
              positId={positId} 
              isPremium={isPremium}
              isPublic={isPublic}
            />
          </div>

          {isPremium ? (
            <div className="space-y-4">
              <div className="h-px bg-border" />
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Share with specific email (Premium feature)
                </p>
                
                <ShareEmailForm 
                  positId={positId} 
                  shareUrl={shareUrl} 
                  onShare={handleRefreshSharedUsers} 
                />

                {sharedUsers && sharedUsers.length > 0 && (
                  <SharedUsersList 
                    positId={positId} 
                    sharedUsers={sharedUsers} 
                    onRemoveAccess={handleRefreshSharedUsers} 
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="h-px bg-border" />
              <PremiumUpgradePrompt />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
